/** Time zone helpers of the calendar (Intl only): known zones, validation, short names. */

/** IANA zones of the browser (suggestions in the settings). */
export function knownTimeZones(): string[] {
  try {
    return (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
}

export function isTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export const browserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Display time zone (setting "Show the calendar in"): the grids work in local wall-clock Dates, so instants are
 * shifted to "the same wall-clock time in the browser's zone" for display and shifted back before saving.
 */
export interface ZoneShift {
  /** Instant → Date whose local fields show the time in the display zone. */
  toWall: (instant: Date | string) => Date;
  /** Wall-clock Date (local fields = display zone time) → the real instant. */
  fromWall: (wall: Date) => Date;
  /** IANA zone sent with recurring/all-day events. */
  zone: string;
}

function wallOf(instant: Date, zone: string): Date {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' })
      .formatToParts(instant)
      .map((p) => [p.type, Number(p.value)]),
  ) as Record<string, number>;
  return new Date(parts.year ?? 1970, (parts.month ?? 1) - 1, parts.day ?? 1, parts.hour ?? 0, parts.minute ?? 0, parts.second ?? 0, instant.getMilliseconds());
}

export function zoneShift(zone: string | null | undefined): ZoneShift {
  const browser = browserTimeZone();
  if (!zone || zone === browser || !isTimeZone(zone)) {
    return { toWall: (instant) => new Date(instant), fromWall: (wall) => new Date(wall), zone: browser };
  }
  return {
    toWall: (instant) => wallOf(new Date(instant), zone),
    fromWall: (wall) => {
      // Two rounds settle DST edges: guess, look at its wall time, correct by the difference.
      let guess = wall.getTime();
      for (let i = 0; i < 2; i++) guess += wall.getTime() - wallOf(new Date(guess), zone).getTime();
      return new Date(guess);
    },
    zone,
  };
}

/** Next full half hour in the display zone, represented by local wall-clock fields like the grids. */
export function nextHalfHour(zone: ZoneShift, instant = new Date()): Date {
  const start = new Date(zone.toWall(instant));
  start.setMinutes(start.getMinutes() < 30 ? 30 : 60, 0, 0);
  // Normalize a suggested nonexistent wall time using the existing DST conversion policy.
  return zone.toWall(zone.fromWall(start));
}

/** Short name of a zone at a date ("CET", "GMT-5"). */
export function zoneLabel(locale: string, zone: string | undefined, at: Date): string {
  try {
    const parts = new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: 'short' }).formatToParts(at);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? zone ?? '';
  } catch {
    return zone ?? '';
  }
}
